// Theme controls: dark-mode toggle + mobile menu.
// Compiled by rollup.config.mjs to:
//   packages/natama_theme/Resources/Public/JavaScript/theme-controls.min.js
//
// The pre-paint script in Layouts/Pages/Default.fluid.html sets the initial
// `data-theme` attribute synchronously to avoid a flash; this module wires
// the interactive controls.

import { dom, library } from '@fortawesome/fontawesome-svg-core';

import { fas } from '@fortawesome/free-solid-svg-icons';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'natama-theme';
const DESKTOP_BREAKPOINT = '(min-width: 768px)';

const root: HTMLElement = document.documentElement;

library.add(fas);

function applyTheme(theme: Theme): void {
	root.setAttribute('data-theme', theme);
}

function currentTheme(): Theme {
	return root.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

function syncToggleButton(button: HTMLElement): void {
	const isDark = currentTheme() === 'dark';
	button.setAttribute('aria-pressed', isDark ? 'true' : 'false');
	button.setAttribute(
		'aria-label',
		isDark ? 'Switch to light mode' : 'Switch to dark mode',
	);
}

function initThemeToggle(): void {
	const toggle = document.querySelector<HTMLElement>('[data-theme-toggle]');
	if (!toggle) return;

	syncToggleButton(toggle);

	toggle.addEventListener('click', () => {
		const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
		applyTheme(next);
		try {
			localStorage.setItem(STORAGE_KEY, next);
		} catch {
			// storage may be unavailable (private mode, disabled, …) — ignore
		}
		syncToggleButton(toggle);
	});
}

function initMobileMenu(): void {
	const menuButton = document.querySelector<HTMLElement>('[data-menu-toggle]');
	const nav = document.querySelector<HTMLElement>('[data-theme-nav]');
	if (!menuButton || !nav) return;

	const close = (): void => {
		nav.classList.remove('is-open');
		menuButton.setAttribute('aria-expanded', 'false');
	};

	const open = (): void => {
		nav.classList.add('is-open');
		menuButton.setAttribute('aria-expanded', 'true');
	};

	menuButton.addEventListener('click', () => {
		if (nav.classList.contains('is-open')) {
			close();
		} else {
			open();
		}
	});

	// Close mobile menu when a link is activated.
	nav.addEventListener('click', (event: MouseEvent) => {
		const target = event.target as Element | null;
		if (target?.closest('a')) {
			close();
		}
	});

	// Close on Escape and return focus to the toggle.
	document.addEventListener('keydown', (event: KeyboardEvent) => {
		if (event.key === 'Escape' && nav.classList.contains('is-open')) {
			close();
			menuButton.focus();
		}
	});

	// Reset state when crossing the desktop breakpoint.
	const mql = window.matchMedia(DESKTOP_BREAKPOINT);
	const handleViewportChange = (
		event: MediaQueryListEvent | MediaQueryList,
	): void => {
		if (event.matches) {
			close();
		}
	};
	mql.addEventListener('change', handleViewportChange);
}

function initSubmenuToggles(): void {
	const mql = window.matchMedia(DESKTOP_BREAKPOINT);

	document
		.querySelectorAll<HTMLElement>('[data-submenu-toggle]')
		.forEach((button) => {
			const submenu = button.nextElementSibling as HTMLElement | null;
			if (!submenu) return;

			button.addEventListener('click', () => {
				// Only act on mobile; desktop uses CSS :hover/:focus-within
				if (mql.matches) return;

				const isOpen = button.getAttribute('aria-expanded') === 'true';
				button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
				submenu.classList.toggle('is-open', !isOpen);
			});
		});

	// Collapse all submenus when switching to desktop
	mql.addEventListener('change', (event) => {
		if (event.matches) {
			document
				.querySelectorAll<HTMLElement>('[data-submenu-toggle]')
				.forEach((button) => {
					button.setAttribute('aria-expanded', 'false');
					const submenu = button.nextElementSibling as HTMLElement | null;
					submenu?.classList.remove('is-open');
				});
		}
	});
}

function initTabs(): void {
	const tabGroups = document.querySelectorAll<HTMLElement>('[data-tabs]');
	if (tabGroups.length === 0) return;

	const activateTab = (group: HTMLElement, nextButton: HTMLElement): void => {
		const buttons = Array.from(
			group.querySelectorAll<HTMLElement>('[data-tab-button]'),
		);
		const panels = Array.from(
			group.querySelectorAll<HTMLElement>('[data-tab-panel]'),
		);
		const targetId = nextButton.getAttribute('aria-controls');

		buttons.forEach((button) => {
			const isActive = button === nextButton;
			button.classList.toggle('is-active', isActive);
			button.setAttribute('aria-selected', isActive ? 'true' : 'false');
			button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
			button.setAttribute('tabindex', isActive ? '0' : '-1');
		});

		panels.forEach((panel) => {
			const isActive = panel.id === targetId;
			panel.classList.toggle('is-active', isActive);
			panel.hidden = !isActive;
		});
	};

	tabGroups.forEach((group) => {
		const buttons = Array.from(
			group.querySelectorAll<HTMLElement>('[data-tab-button]'),
		);
		if (buttons.length < 2) return;

		group.setAttribute('data-tabs-enhanced', 'true');

		const initialButton =
			buttons.find(
				(button) => button.getAttribute('aria-selected') === 'true',
			) ?? buttons[0];
		activateTab(group, initialButton);

		buttons.forEach((button, index) => {
			button.addEventListener('click', () => {
				activateTab(group, button);
				button.focus();
			});

			button.addEventListener('keydown', (event: KeyboardEvent) => {
				let nextIndex = index;

				switch (event.key) {
					case 'ArrowRight':
					case 'ArrowDown':
						nextIndex = (index + 1) % buttons.length;
						break;
					case 'ArrowLeft':
					case 'ArrowUp':
						nextIndex = (index - 1 + buttons.length) % buttons.length;
						break;
					case 'Home':
						nextIndex = 0;
						break;
					case 'End':
						nextIndex = buttons.length - 1;
						break;
					default:
						return;
				}

				event.preventDefault();
				const nextButton = buttons[nextIndex];
				activateTab(group, nextButton);
				nextButton.focus();
			});
		});
	});
}

/**
 * Accessible carousel/slider.
 *
 * Markup contract per slider:
 *   <section class="ce-carousel" data-slider aria-roledescription="carousel" aria-label="…">
 *     <ul class="ce-carousel__track" data-slider-track tabindex="0">
 *       <li class="ce-carousel__slide" role="group" aria-roledescription="slide" aria-label="1 / N">…</li>
 *       …
 *     </ul>
 *     <div class="ce-carousel__controls">
 *       <button data-slider-prev aria-controls="trackId">…</button>
 *       <div class="ce-carousel__dots" role="tablist">
 *         <button data-slider-dot data-slider-index="0" aria-current="true">…</button> …
 *       </div>
 *       <button data-slider-next aria-controls="trackId">…</button>
 *     </div>
 *   </section>
 *
 * Scrolling itself is handled by native CSS scroll-snap on the track
 * (so the slider works without JS and supports touch/mouse/keyboard).
 * JS adds:
 *   - prev/next buttons that scroll to the next snap point
 *   - dots that scroll to a specific slide
 *   - IntersectionObserver-driven sync of dot state and disabled state
 *   - ArrowLeft / ArrowRight / Home / End keyboard navigation on the track
 *   - polite live-region announcement of the current slide
 *   - data-slider-enhanced="true" so SCSS can reveal controls only when JS ran
 */
function initSliders(): void {
	const sections = document.querySelectorAll<HTMLElement>('.ce-carousel');
	sections.forEach(setupSlider);
}

function setupSlider(section: HTMLElement): void {
	const track = section.querySelector<HTMLElement>('[data-slider-track]');
	if (!track) return;

	const slides = Array.from(track.children).filter(
		(node): node is HTMLElement => node instanceof HTMLElement,
	);
	if (slides.length < 2) return;

	const prevBtn =
		section.querySelector<HTMLButtonElement>('[data-slider-prev]');
	const nextBtn =
		section.querySelector<HTMLButtonElement>('[data-slider-next]');
	const dotBtns = Array.from(
		section.querySelectorAll<HTMLButtonElement>('[data-slider-dot]'),
	);

	// Reveal controls (kept hidden via CSS until JS confirms enhancement).
	section.setAttribute('data-slider-enhanced', 'true');

	// Polite live region for screen readers ("Slide X of N").
	const live = document.createElement('div');
	live.className = 'ce-carousel__sr-live';
	live.setAttribute('aria-live', 'polite');
	live.setAttribute('aria-atomic', 'true');
	section.appendChild(live);

	let currentIndex = 0;
	// When the user clicks a dot or presses a key we lock the "active" index
	// briefly so the IntersectionObserver doesn't immediately overwrite it with
	// the leftmost-visible slide while the smooth scroll is still in progress
	// (or when the requested slide is past the max scroll position).
	let pinnedIndex: number | null = null;
	let pinTimer = 0;

	const announce = (index: number): void => {
		live.textContent = `Slide ${index + 1} of ${slides.length}`;
	};

	const updateState = (index: number): void => {
		currentIndex = index;
		dotBtns.forEach((dot, i) => {
			const isActive = i === index;
			dot.classList.toggle('is-active', isActive);
			dot.setAttribute('aria-current', isActive ? 'true' : 'false');
			dot.setAttribute('tabindex', isActive ? '0' : '-1');
		});
		if (prevBtn) prevBtn.disabled = index <= 0;
		if (nextBtn) nextBtn.disabled = index >= slides.length - 1;
	};

	const pinActive = (index: number): void => {
		pinnedIndex = index;
		window.clearTimeout(pinTimer);
		pinTimer = window.setTimeout(() => {
			pinnedIndex = null;
		}, 700);
	};

	const scrollToIndex = (index: number, announceChange = true): void => {
		const clamped = Math.max(0, Math.min(index, slides.length - 1));
		const slide = slides[clamped];
		if (!slide) return;
		// Bounding-rect math works regardless of which element is the slide's
		// offsetParent and accounts for current scroll position.
		const left =
			slide.getBoundingClientRect().left -
			track.getBoundingClientRect().left +
			track.scrollLeft;
		const maxLeft = Math.max(0, track.scrollWidth - track.clientWidth);
		const target = Math.min(left, maxLeft);
		pinActive(clamped);
		updateState(clamped);
		track.scrollTo({ left: target, behavior: 'smooth' });
		if (announceChange) announce(clamped);
	};

	// Track the most-visible slide via IntersectionObserver — keeps state in
	// sync with native scroll/swipe and snap behavior.
	let rafId = 0;
	let lastReported = -1;
	const visibility = new Map<HTMLElement, number>();
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				visibility.set(entry.target as HTMLElement, entry.intersectionRatio);
			});
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(() => {
				// If the user just clicked a dot/pressed a key, respect that
				// intent until the pin expires.
				if (pinnedIndex !== null) return;

				// Pick the leftmost slide that is sufficiently visible.
				let bestIndex = currentIndex;
				for (let i = 0; i < slides.length; i++) {
					const ratio = visibility.get(slides[i]) ?? 0;
					if (ratio >= 0.6) {
						bestIndex = i;
						break;
					}
				}
				if (bestIndex !== lastReported) {
					lastReported = bestIndex;
					updateState(bestIndex);
					announce(bestIndex);
				}
			});
		},
		{ root: track, threshold: [0, 0.25, 0.5, 0.6, 0.75, 1] },
	);
	slides.forEach((slide) => observer.observe(slide));

	prevBtn?.addEventListener('click', () => scrollToIndex(currentIndex - 1));
	nextBtn?.addEventListener('click', () => scrollToIndex(currentIndex + 1));

	dotBtns.forEach((dot, index) => {
		dot.addEventListener('click', () => {
			scrollToIndex(index);
			dot.focus();
		});
	});

	track.addEventListener('keydown', (event: KeyboardEvent) => {
		let target = currentIndex;
		switch (event.key) {
			case 'ArrowRight':
				target = currentIndex + 1;
				break;
			case 'ArrowLeft':
				target = currentIndex - 1;
				break;
			case 'Home':
				target = 0;
				break;
			case 'End':
				target = slides.length - 1;
				break;
			default:
				return;
		}
		event.preventDefault();
		scrollToIndex(target);
	});

	// Initial state without firing the live-region announcement.
	updateState(0);
}

function initFontAwesome(): void {
	dom.i2svg({ node: document.body });
}

function init(): void {
	initThemeToggle();
	initMobileMenu();
	initSubmenuToggles();
	initTabs();
	initSliders();
	initFontAwesome();
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}
